import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Search, Bell, LogOut, User, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { SidebarContent } from "./Sidebar";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import api from "@/lib/api";

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q) { setResults([]); return; }
    timer.current = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {});
    }, 220);
  }, [q]);

  const unread = notifs.filter((n) => !n.read).length;
  const initials = (user?.name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const go = (route) => {
    setSearchOpen(false);
    setQ("");
    navigate(route);
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[#E2E8F0] bg-[#F5F7FA]/80 px-4 backdrop-blur-md sm:px-6">
      {/* Mobile menu */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <button className="rounded-lg p-2 text-[#001A3B] hover:bg-black/5 lg:hidden" data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 border-0 p-0">
          <SidebarContent role={user?.role} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Global search */}
      <button
        onClick={() => setSearchOpen(true)}
        data-testid="global-search-trigger"
        className="flex h-10 w-full max-w-md items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm text-[#8695AB] transition-colors hover:border-[#001A3B]/30"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Rechercher immatriculation, conducteur, contrat…</span>
        <kbd className="hidden rounded border border-[#E2E8F0] bg-[#F5F7FA] px-1.5 py-0.5 font-mono text-[10px] text-[#8695AB] sm:inline">⌘K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-2">
        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="relative rounded-lg p-2 text-[#001A3B] hover:bg-black/5" data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
              {unread > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white">
                  {unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>Notifications</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifs.length === 0 && <div className="px-2 py-6 text-center text-sm text-[#8695AB]">Aucune notification</div>}
            {notifs.slice(0, 6).map((n) => (
              <div key={n.id} className="flex gap-2 px-2 py-2">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.level === "critical" ? "bg-red-500" : n.level === "warning" ? "bg-amber-500" : "bg-emerald-500"}`} />
                <div>
                  <p className="text-sm font-medium text-[#18221F]">{n.title}</p>
                  <p className="text-xs text-[#4A5568]">{n.body}</p>
                </div>
              </div>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/deadlines")} data-testid="notif-see-deadlines">
              Voir toutes les échéances
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white py-1.5 pl-1.5 pr-2.5 hover:border-[#001A3B]/30" data-testid="user-menu-btn">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#001A3B] text-xs font-bold text-white">{initials}</span>
              <div className="hidden text-left sm:block">
                <div className="text-xs font-semibold leading-tight text-[#18221F]">{user?.name}</div>
                <div className="text-[10px] leading-tight text-[#8695AB]">{ROLE_LABELS[user?.role] || user?.role}</div>
              </div>
              <ChevronDown className="hidden h-4 w-4 text-[#8695AB] sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-xs font-normal text-[#8695AB]">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled><User className="mr-2 h-4 w-4" />{ROLE_LABELS[user?.role]}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" />Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput placeholder="Rechercher un véhicule, conducteur, contrat, sinistre…" value={q} onValueChange={setQ} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>Aucun résultat.</CommandEmpty>
          {results.length > 0 && (
            <CommandGroup heading="Résultats">
              {results.map((r) => (
                <CommandItem key={`${r.type}-${r.id}`} value={`${r.title} ${r.subtitle} ${r.id}`} onSelect={() => go(r.route)} data-testid="search-result-item">
                  <span className="mr-2 rounded bg-[#EEF2F8] px-1.5 py-0.5 text-[10px] font-medium text-[#001A3B]">{r.type}</span>
                  <span className="font-medium">{r.title}</span>
                  {r.subtitle && <span className="ml-2 text-xs text-[#8695AB]">{r.subtitle}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </header>
  );
};
